import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDoctorQuery } from '../../hooks/useDoctorQuery';
import { FaFacebook, FaInstagram, FaLinkedin, FaTwitter } from 'react-icons/fa';
import { useLanguage } from '../../context/LanguageContext';
import { slug } from '../../utils/store';

export default function Footer() {
    const { data: doctor } = useDoctorQuery();
    const navigate = useNavigate();
    const { t,isRTL } = useLanguage();

    // Function to handle navigation to sections on home page
    const scrollToSection = (sectionId) => {
        const currentPath = window.location.pathname;
        const homePath = `/`;

        if (currentPath !== homePath) {
            // Navigate to home first, then scroll
            navigate(homePath);
            setTimeout(() => {
                const element = document.getElementById(sectionId);
                if (element) {
                    const offset = window.innerWidth > 768 ? 100 : 250;
                    const y = element.getBoundingClientRect().top + window.scrollY - offset;
                    window.scrollTo({ top: y, behavior: "smooth" });
                }
            }, 100);
        } else {
            // Already on home page, just scroll
            const element = document.getElementById(sectionId);
            if (element) {
                const offset = window.innerWidth > 768 ? 100 : 250;
                const y = element.getBoundingClientRect().top + window.scrollY - offset;
                window.scrollTo({ top: y, behavior: "smooth" });
            }
        }
    };

    return (
        <footer className="bg-goldy-hover text-white p-6 md:p-8">
            <div className="mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
                <div>
                    <h1 className={`flex gap-2 font-bold text-2xl ${isRTL?"flex-row-reverse justify-end text-right":"flex-row justify-start text-left"}`}>
                        <p>{doctor?.name} 
                            </p>{t('footer.center')}
                    </h1>
                    <p className="text-sm">
                        {doctor?.slogan}
                    </p>
                </div>

                {/* Quick Links */}
                <div>
                    <h2 className="text-lg font-semibold mb-2">{t('footer.quickLinks')}</h2>
                    <ul className="space-y-1 text-sm">
                        <li>
                            <button 
                                onClick={() => scrollToSection('about')}
                                className="hover:underline text-left"
                            >
                                {t('footer.about')}
                            </button>
                        </li>
                        <li>
                            <button 
                                onClick={() => scrollToSection('team')}
                                className="hover:underline text-left"
                            >
                                {t('footer.services')}
                            </button>
                        </li>
                        <li>
                            <button 
                                onClick={() => scrollToSection('contact')}
                                className="hover:underline text-left"
                            >
                                {t('footer.contact')}
                            </button>
                        </li>
                        <li>
                            <button 
                                onClick={() => scrollToSection('reviews')}
                                className="hover:underline text-left"
                            >
                                {t('footer.reviews')}
                            </button>
                        </li>
                    </ul>
                </div>

                {/* Contact Info */}
                {!doctor?.isLocked && (
                    <div>
                        <h2 className="text-lg font-semibold mb-2">{t('footer.contactSection')}</h2>
                        <ul className="space-y-1 text-sm">
                            <li>{t('footer.phone')}: {doctor?.contactPhone || t('footer.contactPhonePlaceholder')}</li>
                            <li>{t('footer.email')}: {doctor?.contactEmail || t('footer.contactEmailPlaceholder')}</li>
                        </ul>
                        {/* Social Media Icons */}
                        <div className="flex space-x-4 mt-4">
                            {doctor?.socialMedia?.facebook && (
                                <a 
                                    href={doctor.socialMedia.facebook} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    aria-label="Facebook" 
                                    className="text-gray-400 hover:text-teal-300 transition-colors"
                                >
                                    <FaFacebook className="w-6 h-6" />
                                </a>
                            )}
                            {doctor?.socialMedia?.twitter && (
                                <a 
                                    href={doctor.socialMedia.twitter} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    aria-label="Twitter" 
                                    className="text-gray-400 hover:text-teal-300 transition-colors"
                                >
                                    <FaTwitter className="w-6 h-6" />
                                </a>
                            )}
                            {doctor?.socialMedia?.instagram && (
                                <a 
                                    href={doctor.socialMedia.instagram} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    aria-label="Instagram" 
                                    className="text-gray-400 hover:text-teal-300 transition-colors"
                                >
                                    <FaInstagram className="w-6 h-6" />
                                </a>
                            )}
                            {doctor?.socialMedia?.linkedin && (
                                <a 
                                    href={doctor.socialMedia.linkedin} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    aria-label="LinkedIn" 
                                    className="text-gray-400 hover:text-teal-300 transition-colors"
                                >
                                    <FaLinkedin className="w-6 h-6" />
                                </a>
                            )}
                        </div>
                    </div>
                )}
            </div>
            {/* Copyright */}
            <div className="mt-8 text-center text-sm border-t pt-4">
                {t('footer.copyright')}
            </div>
        </footer>
    );
}